import React, { useEffect, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Loader2 } from "lucide-react";

interface MinBudgetModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Channel label for the primary CTA ("Hand-to-hand" / "Door-to-Door"). */
  channelLabel: string;
  /** Market minimum, pre-formatted ("$5,000" / "$20,000"). */
  minBudgetFormatted: string;
  /** Start a fresh EDDM campaign (AC34). Resolves on navigate, rejects on failure. */
  onSwitchToEddm: () => Promise<void>;
}

// AC33 — US-only modal shown when the minimum distributors/areas are met but the
// campaign total cost is below the market minimum budget. Offers a switch to a
// fresh EDDM campaign (secondary) or staying to add more coverage (primary).
// Built on radix primitives directly, matching the area-removal confirm: a light
// scrim and a subtle centred surface.
export const MinBudgetModal = ({
  open,
  onOpenChange,
  channelLabel,
  minBudgetFormatted,
  onSwitchToEddm,
}: MinBudgetModalProps) => {
  const [isSwitching, setIsSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset the in-flight/error state whenever the modal reopens.
  useEffect(() => {
    if (!open) {
      setIsSwitching(false);
      setError(null);
    }
  }, [open]);

  // Guard against double-clicks creating parallel EDDM campaigns: disable the
  // button while the request is in flight, and surface a visible error if it
  // fails (success navigates away, so no reset is needed there).
  const handleSwitch = async () => {
    if (isSwitching) return;
    setIsSwitching(true);
    setError(null);
    try {
      await onSwitchToEddm();
    } catch {
      setIsSwitching(false);
      setError("Couldn't start an EDDM campaign. Please try again.");
    }
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="cb-alert-overlay fixed inset-0 z-[1350] bg-[rgba(0,0,0,0.25)]" />
        <DialogPrimitive.Content
          data-cypress-id="targeting-min-budget-modal"
          className="cb-alert fixed left-1/2 top-1/2 z-[1350] w-full max-w-[460px] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-solid border-border bg-background p-6 shadow-xl focus:outline-none"
        >
          <DialogPrimitive.Title className="text-lg font-semibold text-foreground">
            Campaign is below the {minBudgetFormatted} minimum
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
            Add more distributions or coverage to reach the minimum. Want to work with a lighter budget? EDDM (Every
            Door Direct Mail) starts at $1,000.
          </DialogPrimitive.Description>
          {error && (
            <p className="mt-3 text-sm text-destructive" role="alert" data-cypress-id="targeting-eddm-error">
              {error}
            </p>
          )}
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={handleSwitch}
              disabled={isSwitching}
              data-cypress-id="targeting-switch-to-eddm"
              className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-solid border-[#1a2547] bg-white px-4 text-sm font-medium text-[#1a2547] hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSwitching && <Loader2 className="size-4 animate-spin" />}
              Switch to EDDM
            </button>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={isSwitching}
              data-cypress-id="targeting-budget-continue"
              className="inline-flex h-9 items-center justify-center rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Continue with {channelLabel}
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
